import {
    useEffect,
    RefObject,
    type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

export function hasModifierKeyPressed(
    keyboardEvent: KeyboardEvent | ReactKeyboardEvent,
): boolean {
    return (
        keyboardEvent.ctrlKey || keyboardEvent.metaKey || keyboardEvent.altKey
    );
}

export function isEditableOrInteractiveTarget(
    keyboardEvent: KeyboardEvent | ReactKeyboardEvent,
): boolean {
    const target = keyboardEvent.target;
    return (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLButtonElement ||
        (target instanceof HTMLElement && target.isContentEditable)
    );
}

export type SubmitOnEnterKeyDownOptions = {
    submitOnEnter: boolean;
    onSubmit?: () => void;
    submitDisabled: boolean;
    submitInProgress: boolean;
};

export function createSubmitOnEnterKeyDownHandler(
    options: SubmitOnEnterKeyDownOptions,
): (keyboardEvent: ReactKeyboardEvent) => void {
    return (keyboardEvent) => {
        const { submitOnEnter, onSubmit, submitDisabled, submitInProgress } =
            options;
        if (!submitOnEnter || !onSubmit || submitDisabled || submitInProgress) {
            return;
        }
        if (keyboardEvent.key !== 'Enter' || keyboardEvent.shiftKey) return;
        const target = keyboardEvent.target;
        if (target instanceof HTMLTextAreaElement) return;
        if (target instanceof HTMLButtonElement) return;
        if (target instanceof HTMLAnchorElement) return;
        if (target instanceof HTMLVideoElement) return;
        if (target instanceof HTMLElement && target.closest('video')) {
            return;
        }
        keyboardEvent.preventDefault();
        onSubmit();
    };
}

export function useFocusOnKey(
    key: string,
    inputRef: RefObject<HTMLInputElement | HTMLTextAreaElement | null>,
    applyFocus?: () => void,
) {
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (hasModifierKeyPressed(e)) return;
            const keyMatches =
                e.key === key ||
                e.key === key.toUpperCase() ||
                e.code === `Key${key.toUpperCase()}`;
            if (keyMatches && !isEditableOrInteractiveTarget(e)) {
                e.preventDefault();
                if (applyFocus) {
                    applyFocus();
                } else {
                    inputRef.current?.focus();
                }
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [key, inputRef, applyFocus]);
}

export function useKeyboardShortcut(
    shortcut: string[] | undefined,
    callback: () => void,
) {
    useEffect(() => {
        if (!shortcut || shortcut.length === 0) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (isEditableOrInteractiveTarget(e)) return;

            const { modifiers, key: expectedKey } = shortcut.reduce(
                (acc, key) => {
                    const lowerKey = key.toLowerCase();

                    if (lowerKey === 'ctrl' || lowerKey === 'control') {
                        acc.modifiers.add('ctrl');
                    } else if (lowerKey === 'shift') {
                        acc.modifiers.add('shift');
                    } else if (lowerKey === 'alt') {
                        acc.modifiers.add('alt');
                    } else if (lowerKey === 'meta' || lowerKey === 'cmd') {
                        acc.modifiers.add('meta');
                    } else {
                        acc.key = lowerKey;
                    }

                    return acc;
                },
                {
                    modifiers: new Set<string>(),
                    key: null as string | null,
                },
            );

            if (expectedKey === null) {
                return;
            }

            const keyMatches =
                e.key.toLowerCase() === expectedKey ||
                e.code === `Key${expectedKey.toUpperCase()}` ||
                e.code === `Digit${expectedKey}`;
            if (!keyMatches) {
                return;
            }

            const ctrlExpected = modifiers.has('ctrl');
            const shiftExpected = modifiers.has('shift');
            const altExpected = modifiers.has('alt');
            const metaExpected = modifiers.has('meta');

            const ctrlMetaMatch =
                ctrlExpected && metaExpected
                    ? e.metaKey && !e.ctrlKey
                    : ctrlExpected
                      ? e.ctrlKey || e.metaKey
                      : metaExpected
                        ? e.metaKey && !e.ctrlKey
                        : !e.ctrlKey && !e.metaKey;

            const shiftMatch = shiftExpected ? e.shiftKey : !e.shiftKey;
            const altMatch = altExpected ? e.altKey : !e.altKey;

            if (keyMatches && ctrlMetaMatch && shiftMatch && altMatch) {
                e.preventDefault();
                callback();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [shortcut, callback]);
}
